import type { ProjectNanoId } from '@composio/types'
import type { AxiosError } from 'axios'
import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Button } from 'design-system/ui/atoms/button'
import {
  AlertCircleIcon,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  MoveRightIcon,
  PencilIcon,
  RefreshCwIcon,
  Trash2Icon,
} from 'lucide-react'
import {
  adjectives,
  animals,
  colors,
  uniqueNamesGenerator,
} from 'unique-names-generator'
import { z } from 'zod'

import { DialogBoxCenter } from '~/design-system/atom/alert-dialog-sheet'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
} from '~/design-system/atom/dialog'
import { Input } from '~/design-system/atom/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/design-system/atom/select'
import { useToast } from '~/design-system/atom/use-toast'
import {
  deleteOrgProjectByProjectIdApiKeysRemoveById,
  getOrgProjectByProjectIdApiKeysList,
  postOrgProjectByProjectIdApiKeysCreate,
} from '~/generated/client'
import {
  useGetOrgApiKeyQuery,
  useRegenerateOrgApiKeyMutation,
} from '~/hooks/query/settings'
import {
  useGetAllTeamMembers,
  useInviteTeamMemberMutation,
  useModifyTeamMemberRoleMutation,
  useRemoveTeamMemberMutation,
} from '~/hooks/query/team-members'
import { useLocalStorage } from '~/hooks/utils/useLocalStorage'
import { scrollBarStyle } from '~/ui/constants/style/common'
import { LoadingState } from '~/ui/containers/base/emptyStates'
import { useGetUserInfo } from '~/ui/containers/base/hooks'
import { useProjects } from '~/ui/containers/base/sidebar/hooks'
import { TriggerWebhookSettingBlock } from '~/ui/screens/settings/TriggerWebhook'
import { copyText } from '~/utils/dom'
import { getCurrentClientSideHost } from '~/utils/global'
import { If } from '~/utils/reactComponent'
import { RightSideContent } from '../../containers/base/base'
import { EventWebHookAndSettingBlock } from './AdminWebhook'
import { ApiKeyRow } from './components/APIKeyRow'
import { SettingsEmptyState } from './components/EmptyState'
import { MemberRow } from './components/MemberRow'
import { OpenAPISpec } from './openapi_spec'
import { WebhookSecretBlock } from './WebhookSecret'

// API Keys Query and Mutation Hooks
const QUERY_KEYS_API_KEYS = {
  GET_API_KEYS: 'GET_API_KEYS',
  CREATE_API_KEY: 'CREATE_API_KEY',
}

const STALE_TIME = 1000 * 60 * 5

function useGetAPIKeysQuery(projectId?: ProjectNanoId) {
  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: [QUERY_KEYS_API_KEYS.GET_API_KEYS, projectId],
    queryFn: () =>
      getOrgProjectByProjectIdApiKeysList({ projectId: projectId! }),
    staleTime: STALE_TIME,
    enabled: Boolean(projectId),
  })

  return {
    data: data?.items,
    isLoading: isLoading,
    isError: isError,
    isSuccess: isSuccess,
  }
}

function useDeleteAPIKeyMutation() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteOrgProjectByProjectIdApiKeysRemoveById,
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'API key deleted successfully',
      })
      queryClient.invalidateQueries([QUERY_KEYS_API_KEYS.GET_API_KEYS])
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast({
        title: 'Error',
        description:
          'Failed to delete API key - ' + error?.response?.data?.message,
        variant: 'destructive',
      })
    },
  })
}

function useCreateAPIKeyMutation() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postOrgProjectByProjectIdApiKeysCreate,
    onSuccess: async () => {
      await queryClient.invalidateQueries([QUERY_KEYS_API_KEYS.GET_API_KEYS])
      toast({
        title: 'Success',
        description: 'API key was added successfully',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to add API key',
        variant: 'destructive',
      })
    },
  })
}

export const ApprovalPopup = ({
  heading = 'Are you sure about removal?',
  description = 'Click remove button to proceed.',
  approveButtonText = 'Remove',
  onApprove,
  setOpen,
}: {
  heading: string
  description: string
  approveButtonText: string
  onApprove: () => Promise<void>
  setOpen: (e: boolean) => void
}) => {
  const isDelete =
    approveButtonText === 'Proceed with delete' ||
    approveButtonText === 'Remove' ||
    approveButtonText === 'Delete API Key'
  return (
    <DialogBoxCenter
      open={true}
      setOpen={setOpen.bind(this)}
      className={clsx(
        ' max-h-[90vh] w-[580px] overflow-y-scroll bg-[#fff] px-0',
        scrollBarStyle,
      )}
    >
      <DialogHeader>
        <DialogTitle className="mt-1e">
          <div className=" leading-non flex   flex-col">
            <div className=" mb-2  text-[17px] font-[500] text-black-200">
              <AlertCircleIcon
                height={24}
                width={24}
                className="mb-6 mr-2"
                strokeWidth={1.5}
              />
              {heading}
            </div>
            <div className=" text-[14px] font-[400] leading-[25px] text-black-700">
              {description}
            </div>
          </div>
        </DialogTitle>
      </DialogHeader>
      <DialogFooter className="flex justify-start  gap-2 border-t-[0px] !py-6   font-gilroy text-[13px] tracking-[.35px] text-black-300">
        <Button
          variant={isDelete ? 'destructive' : 'primary'}
          size="36"
          onClick={() => {
            onApprove()
            setOpen(false)
          }}
        >
          {isDelete ? (
            <Trash2Icon className="mr-2" height={14} width={14} />
          ) : (
            <PencilIcon className="mr-2" height={14} width={14} />
          )}

          {approveButtonText}
        </Button>
        <Button
          variant="secondary"
          size="36"
          onClick={() => {
            setOpen(false)
          }}
        >
          Go back <MoveRightIcon className="ml-2" height={14} width={14} />
        </Button>
      </DialogFooter>
    </DialogBoxCenter>
  )
}

export const APIKeys = () => {
  const { getItemSpecific } = useLocalStorage()
  // TODO: Remove this once we have a better way to get the project id @apoorv
  const projectId = getItemSpecific('current.project')
  const { selectedProjectItem } = useProjects()
  const selectedProjectId = projectId ?? selectedProjectItem?.id

  const { authSessionInfo, isLoading: isLoadingUserInfo } = useGetUserInfo()

  const { data: apiKeys, isLoading } = useGetAPIKeysQuery(selectedProjectId)

  const debuggingInformation =
    isLoadingUserInfo || isLoading
      ? 'Loading system information...'
      : `@project_id:${selectedProjectId} | @org_id:${authSessionInfo?.project?.org.id}\n@org_member_email:${authSessionInfo?.org_member?.email}`

  const { mutate: deleteAPIKeyMutation } = useDeleteAPIKeyMutation()

  const { toast } = useToast()

  const [deletionStageAPIKey, setDeletionStageAPIKey] = useState<string | null>(
    null,
  )
  const handleDeleteApiKey = (projectId: ProjectNanoId, id: string) => {
    deleteAPIKeyMutation({ projectId: projectId ?? '', id: id })
  }

  const [showAddApiKey, setShowAddApiKey] = useState(false)

  const onCopy = (key: string) => {
    copyText(key)
    toast({
      title: 'Copied',
      variant: 'success',
      description: 'API Key copied to clipboard',
    })
  }

  return (
    <div className="m-auto !mb-8 !mt-4 flex w-[900px] flex-col items-start">
      {!!deletionStageAPIKey && (
        <ApprovalPopup
          heading="Are you sure about deleting API key?"
          description="This action cannot be done and potentially break your composio client."
          approveButtonText="Delete API Key"
          onApprove={async () => {
            if (selectedProjectId) {
              handleDeleteApiKey(selectedProjectId, deletionStageAPIKey)
            } else {
              toast({
                title: 'Error',
                description: 'Please select a project',
                variant: 'destructive',
              })
            }
          }}
          setOpen={() => {
            setDeletionStageAPIKey(null)
          }}
        />
      )}
      {showAddApiKey && <AddAPIKeyPopup setOpen={setShowAddApiKey} />}

      <div className="flex w-full items-center justify-between ">
        <div className="flex flex-col items-baseline">
          <div className=" text-[16px] font-[500] text-black-200">
            Support debugging information
          </div>
          <p className="mb-0 mt-1 text-[14px] font-[400] text-gray-700">
            Having trouble? Share these details with support for faster help.
          </p>
        </div>
        <Button
          variant="secondary"
          size="36"
          onClick={() => {
            copyText(debuggingInformation)
            toast({
              title: 'Copied',
              description: 'Support debugging information copied to clipboard',
              variant: 'success',
            })
          }}
          uplifted={true}
        >
          <CopyIcon className="mr-2" height={14} width={14} />
          Copy Details
        </Button>
      </div>
      <hr className="my-4 mt-4 h-[1px] w-full bg-grey-300" />
      <textarea
        className="mb-[44px] min-h-[60px] w-full rounded-lg border border-gray-900 p-4 font-mono text-[12px] font-[400] text-black-100"
        value={`${debuggingInformation}`}
        readOnly
        placeholder="Loading system information..."
        aria-label="System debugging information"
      />

      <div className="flex w-full items-center justify-between ">
        <div className="flex flex-col items-baseline">
          <div className=" text-[16px] font-[500] text-black-200">
            Project API Keys
          </div>
          <p className="mb-0 mt-1 text-[14px] font-[400] text-gray-700">
            Project keys help manage a single project with seamless access and
            collaboration.
          </p>
        </div>
        <Button
          variant="primary"
          size="36"
          onClick={() => setShowAddApiKey(true)}
          uplifted={false}
        >
          Add API Key
        </Button>
      </div>
      <hr className="my-4 mt-4 h-[1px] w-full bg-grey-300" />
      <If condition={!!apiKeys && apiKeys.length > 0 && !isLoading}>
        <>
          <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="grid grid-cols-[1fr_1fr_1.2fr] gap-4 border-b border-gray-200 bg-gray-50 p-4 ">
              <div className="text-sm font-medium text-gray-500">Name</div>
              <div className="text-sm font-medium text-gray-500">Last Used</div>
              <div className="text-sm font-medium text-gray-500">API Key</div>
            </div>
            <div className="divide-y divide-gray-200">
              {apiKeys?.map((key) => (
                <ApiKeyRow
                  name={key.name}
                  key={key.key}
                  apiKey={key.key}
                  onCopy={() => onCopy(key.key)}
                  lastUsed={
                    key?.last_used
                      ? new Date(key.last_used).toLocaleString()
                      : 'Never'
                  }
                  onDelete={() => setDeletionStageAPIKey(key.id)}
                />
              ))}
            </div>
          </div>
        </>
      </If>
      <If condition={isLoading}>
        <div className="mt-4 h-[100px] w-full">
          <LoadingState height={200} />
        </div>
      </If>
      <If condition={apiKeys?.length === 0 && !isLoading}>
        <SettingsEmptyState
          title="No API keys found"
          description="Add API keys to get started"
        />
      </If>
    </div>
  )
}

interface APIKeyDisplayProps {
  apiKey: string
  isVisible: boolean
  onToggleVisibility: () => void
}

export const AdminApiKeys = () => {
  const {
    data: orgApiKey,
    isLoading,
    refetch,
    isError,
  } = useGetOrgApiKeyQuery()
  const { toast } = useToast()
  const [isVisible, setIsVisible] = useState(false)

  const {
    mutate: regenerateOrgApiKeyMutation,
    isLoading: isRegeneratingOrgApiKey,
  } = useRegenerateOrgApiKeyMutation({
    onSuccess: () => {
      refetch().then(() => {
        toast({
          title: 'Org API Key regenerated',
          description: 'Org API Key regenerated successfully',
        })
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error regenerating Org API Key',
        variant: 'destructive',
      })
    },
  })

  function APIKeyDisplay({
    apiKey,
    isVisible,
    onToggleVisibility,
  }: APIKeyDisplayProps) {
    return (
      <div className="flex w-[500px] max-w-2xl items-center gap-2 rounded-lg border bg-gray-50 px-2 shadow-sm">
        <div className="flex-1 truncate font-mono text-sm text-gray-800">
          {isVisible
            ? apiKey
            : '•'.repeat(apiKey.length - 4) + apiKey.slice(-4)}
        </div>
        <button
          onClick={onToggleVisibility}
          className="p-2 text-gray-500 transition-colors hover:text-gray-700 "
          aria-label={isVisible ? 'Hide API key' : 'Show API key'}
        >
          {isVisible ? (
            <EyeOffIcon className="h-4 w-4" />
          ) : (
            <EyeIcon className="h-4 w-4" />
          )}
        </button>
        <button
          onClick={() => {
            copyText(apiKey)
            toast({
              title: 'Copied',
              description: 'API Key copied to clipboard',
            })
          }}
        >
          <CopyIcon className="h-4 w-4 text-gray-500 transition-colors hover:text-gray-700" />
        </button>
      </div>
    )
  }

  const BaseSkeleton = useCallback(
    ({ children }: { children: React.ReactNode }) => {
      return (
        <div className="m-auto !mt-4 flex w-[900px] flex-col items-start">
          <div className="flex w-full  flex-col items-baseline justify-between">
            <div className=" text-[16px] font-[500] text-black-200">
              Organization API Keys
            </div>
            <p className="mb-0 mt-1 text-[14px] font-[400] text-gray-700">
              Organization keys help manage multiple projects with seamless
              access and collaboration.
            </p>
          </div>
          <hr className="my-4 mt-4 h-[1px] w-full bg-grey-300" />
          {children}
        </div>
      )
    },
    [],
  )

  if (isError) {
    return (
      <BaseSkeleton>
        <div className="flex w-full items-center justify-between">
          <div className=" text-[12px] font-[500] text-black-200">
            No Org API Key found
          </div>
        </div>
      </BaseSkeleton>
    )
  }

  if (isLoading) {
    return (
      <BaseSkeleton>
        <div className="mb-4 h-2 w-3/4 rounded bg-gray-200"></div>
        <div className="mb-4 h-2 w-1/2 rounded bg-gray-200"></div>
        <div className="h-2 w-full rounded bg-gray-200"></div>
      </BaseSkeleton>
    )
  }

  if (!isLoading && !orgApiKey?.org_api_key) {
    return (
      <BaseSkeleton>
        <div className="flex w-full items-center justify-between">
          <div className="mb-4 h-2 w-3/4 rounded bg-gray-200">
            Please contact support or admin to get an ORG API keys
          </div>
        </div>
      </BaseSkeleton>
    )
  }

  return (
    <BaseSkeleton>
      <div className="w-full space-y-4 rounded-lg border border-gray-200 p-5 shadow-sm">
        <div className="sm:flex-row sm:items-center flex gap-4">
          <APIKeyDisplay
            apiKey={orgApiKey?.org_api_key}
            isVisible={isVisible}
            onToggleVisibility={() => setIsVisible(!isVisible)}
          />
          <Button
            size="36"
            onClick={() => {
              regenerateOrgApiKeyMutation()
            }}
            uplifted={false}
          >
            <RefreshCwIcon
              className={`mr-2 h-4 w-4 ${isRegeneratingOrgApiKey ? 'animate-spin' : ''}`}
            />
            Regenerate API Key
          </Button>
        </div>
      </div>
    </BaseSkeleton>
  )
}

export const AddAPIKeyPopup = ({
  setOpen,
}: {
  setOpen: (e: boolean) => void
}) => {
  const { getItemSpecific } = useLocalStorage()
  const projectId = getItemSpecific('current.project')
  const { selectedProjectItem } = useProjects()
  const selectedProjectId = projectId ?? selectedProjectItem?.id
  const { mutate: createAPIKeyMutation } = useCreateAPIKeyMutation()

  const [apiKeyName, setApiKeyName] = useState('')

  const { toast } = useToast()

  const handleAddApiKey = (projectId: ProjectNanoId) => {
    const shortName = uniqueNamesGenerator({
      dictionaries: [adjectives, animals, colors], // colors can be omitted here as not used
      length: 1,
    })

    const randomName = `api_key_${shortName}`
    createAPIKeyMutation({
      projectId: projectId,
      requestBody: { name: apiKeyName ?? randomName },
    })
    setOpen(false)
  }

  return (
    <DialogBoxCenter
      open={true}
      setOpen={setOpen}
      className={clsx(
        'max-h-[90vh] w-[500px] overflow-hidden rounded-[12px] bg-[#fff]',
        scrollBarStyle,
      )}
    >
      <DialogHeader>
        <DialogTitle>API Keys</DialogTitle>
        <DialogSubtitle>
          Generate API keys to use with SDK or Call APIs
        </DialogSubtitle>
      </DialogHeader>
      <DialogDescription>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            API Key Name (optional)
          </label>
          <Input
            className="h-[36px] w-[300px] flex-auto rounded-[6px]"
            placeholder="Enter API Key Name"
            value={apiKeyName}
            onChange={(e) => setApiKeyName(e.target.value)}
          />
        </div>
      </DialogDescription>
      <DialogFooter>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="40"
            className="h-[36px] w-[110px]"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="40"
            className="h-[36px] w-[110px]"
            onClick={() => {
              if (selectedProjectId) {
                handleAddApiKey(selectedProjectId)
              } else {
                toast({
                  title: 'Error',
                  description: 'Please select a project',
                  variant: 'destructive',
                })
              }
            }}
          >
            Add
          </Button>
        </div>
      </DialogFooter>
    </DialogBoxCenter>
  )
}

export const InviteTeamMembers = () => {
  const { data: teamMembers, isLoading } = useGetAllTeamMembers()

  const { mutate: modifyTeamMemberRole } = useModifyTeamMemberRoleMutation()

  const [showAddTeamMember, setShowAddTeamMember] = useState(false)

  const filteredTeamMembers = teamMembers || []

  const [teamMemberForDelete, setTeamMemberForDelete] = useState<string | null>(
    null,
  )

  return (
    <div className="m-auto !mt-1 flex w-[900px] flex-col items-start">
      {!!teamMemberForDelete && (
        <DeleteTeamMemberPopup
          teamMemberForDelete={teamMemberForDelete}
          setOpen={() => {
            setTeamMemberForDelete(null)
          }}
        />
      )}
      {showAddTeamMember && (
        <AddTeamMemberPopup setOpen={setShowAddTeamMember} />
      )}
      <div className="flex w-full items-center justify-between  text-[16px] font-[600] text-black-200">
        <div>Team Members</div>
        <Button
          variant="primary"
          size="36"
          onClick={() => setShowAddTeamMember(true)}
          uplifted={false}
        >
          Add Team Member
        </Button>
      </div>
      <hr className="my-1 mt-4 h-[1px] w-full bg-grey-300" />
      <div className="mt-4 flex w-full flex-col gap-4">
        {!!filteredTeamMembers?.length ? (
          <>
            <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="grid grid-cols-[1fr_1fr_1fr_1.5fr] gap-4 border-b border-gray-200 bg-gray-50 p-4 ">
                <div className="text-sm font-medium text-gray-500">Email</div>
                <div className="text-sm font-medium text-gray-500">Role</div>
                <div className="text-sm font-medium text-gray-500">
                  Created At
                </div>
                <div className="text-sm font-medium text-gray-500">
                  Updated At
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {filteredTeamMembers?.map((member) => (
                  <MemberRow
                    key={member.id}
                    name={member.name}
                    createdAt={member.created_at || ''}
                    updatedAt={member.updated_at || ''}
                    role={member.role}
                    email={member.email}
                    id={member.id}
                    modifyTeamMemberRole={modifyTeamMemberRole}
                    onDelete={() => setTeamMemberForDelete(member.id)}
                  />
                ))}
              </div>
            </div>
          </>
        ) : isLoading ? (
          <div className="mt-4 h-[100px] w-full">
            <LoadingState height={200} />
          </div>
        ) : (
          <SettingsEmptyState
            title="No team members found"
            description="Invite team members to get started"
          />
        )}
      </div>
    </div>
  )
}

export const AddTeamMemberPopup = ({
  setOpen,
}: {
  setOpen: (e: boolean) => void
}) => {
  const { mutate: inviteTeamMemberMutation } = useInviteTeamMemberMutation()
  const { toast } = useToast()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'admin' | 'developer'>('admin')
  const [isInviting, setIsInviting] = useState(false)

  const handleInviteTeamMember = () => {
    if (isInviting) return

    const currentHost = getCurrentClientSideHost()

    try {
      z.string().email().parse(email)
      inviteTeamMemberMutation(
        {
          requestBody: {
            email: email,
            name: name,
            verify_host: `${currentHost}/verify`,
            role: role,
          },
        },
        {
          onSuccess: () => {
            setIsInviting(false)
            setOpen(false)
          },
        },
      )
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: 'Invalid input',
          description: 'Please enter a valid email',
          variant: 'destructive',
        })
      }
    }
  }

  return (
    <DialogBoxCenter
      open={true}
      setOpen={setOpen}
      className={clsx(
        ' max-h-[90vh] w-[600px] max-w-[600px] overflow-hidden rounded-[12px] bg-[#fff]',
        scrollBarStyle,
      )}
    >
      <DialogHeader>
        <DialogTitle> Add a team member</DialogTitle>
        <DialogSubtitle>
          Enter the member's email, and we'll send them an invite link.{' '}
        </DialogSubtitle>
      </DialogHeader>
      <DialogDescription className="!mt-[28px] px-6   font-gilroy text-[13px] tracking-[.35px] text-black-300">
        <div className="flex gap-4">
          <div className="flex w-full max-w-[full] flex-col gap-4">
            <div className="flex w-full max-w-[full] flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="flex w-full flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <Input
                    className="h-[36px] min-w-[140px] flex-auto rounded-[6px]"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="flex w-full flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <Input
                    className="h-[36px] min-w-[140px] flex-auto rounded-[6px]"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex w-full flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">
                  Role
                </label>
                <Select
                  onValueChange={(e) => {
                    // Handle role change logic here
                    setRole(e as 'admin' | 'developer')
                  }}
                  value={role}
                >
                  <SelectTrigger className="h-[36px] w-[216px]">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="z-[300]">
                    <SelectGroup>
                      <SelectItem value="admin">Admin</SelectItem>
                      {/* <SelectItem value="developer">Developer</SelectItem> */}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </DialogDescription>
      <DialogFooter>
        <div className="flex gap-4">
          <Button
            variant="secondary"
            size="40"
            className="h-[36px] w-[110px]"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="40"
            className="h-[36px] w-[110px]"
            onClick={handleInviteTeamMember}
            disabled={isInviting}
          >
            Invite
          </Button>
        </div>
      </DialogFooter>
    </DialogBoxCenter>
  )
}

export const DeleteTeamMemberPopup = ({
  teamMemberForDelete,
  setOpen,
}: {
  teamMemberForDelete: string
  setOpen: (e: boolean) => void
}) => {
  const { mutate: removeTeamMemberMutation } = useRemoveTeamMemberMutation()

  const handleRemoveTeamMember = (id: string) => {
    removeTeamMemberMutation({ id })
  }

  return (
    <ApprovalPopup
      heading="Are you sure about removal?"
      description="Click remove button to proceed."
      approveButtonText="Remove"
      onApprove={async () => {
        handleRemoveTeamMember(teamMemberForDelete)
      }}
      setOpen={() => {
        setOpen(false)
      }}
    />
  )
}

export function CompleteSettingPage() {
  return (
    <RightSideContent header="Developer" showTopBar={true}>
      <div className="flex flex-col gap-4 px-8 pb-[32px] pt-6">
        <APIKeys />
        <InviteTeamMembers />
        <AdminApiKeys />
      </div>
    </RightSideContent>
  )
}

export function EventsAndTrigger() {
  return (
    <RightSideContent header="Events & Triggers" showTopBar={true}>
      <div className="mb-[32px] flex flex-col gap-5 px-8 pt-6">
        <TriggerWebhookSettingBlock />
        <EventWebHookAndSettingBlock />
        <WebhookSecretBlock />
      </div>
    </RightSideContent>
  )
}

export function OpenAPIUploadBLock() {
  return (
    <RightSideContent
      headerLinks={[
        {
          link: '/apps',
          text: 'Tools',
          icon: null,
        },
        {
          link: '#',
          text: 'Custom tools',
          icon: null,
        },
      ]}
      showTopBar={true}
    >
      <div className="px-8 pt-6">
        <OpenAPISpec />
      </div>
    </RightSideContent>
  )
}
